import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "./ui/sonner";
import { ClipboardCheck, Send, Bell, ShieldCheck, CalendarDays, CalendarCheck } from "lucide-react";
import { SignLinkBlock, StartWorkBlock, QuoteReviewBlock, CompletionDocsTeam } from "./SubcontractTeamOps";
import { StageInspectDialog } from "./subcontract/StageInspectDialog";
import { SubcontractAccountStatus } from "./subcontract/SubcontractAccountStatus";
import { UnsignedPaymentApproval } from "./subcontract/UnsignedPaymentApproval";

const STAGE_LABEL = {
  pending: { zh: "未开始", en: "Pending", cls: "bg-slate-100 text-slate-500" },
  notified: { zh: "待填工期", en: "Notified", cls: "bg-blue-100 text-blue-700" },
  in_progress: { zh: "施工中", en: "In progress", cls: "bg-amber-100 text-amber-700" },
  completed_pending: { zh: "完工待检", en: "Awaiting inspection", cls: "bg-purple-100 text-purple-700" },
  inspecting: { zh: "检查中", en: "Inspecting", cls: "bg-orange-100 text-orange-700" },
  closed: { zh: "已验收", en: "Closed", cls: "bg-green-100 text-green-700" },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** 合同、施工、资料按业务分区显示；隐藏分区保持表单草稿，权限仍沿用各动作的角色规则。 */
export default function SubcontractTeamPanel({ sc, reload, section = "" }) {
  const { lang, user, api } = useApp();
  const zh = lang === "zh";
  const canManage = ["admin", "eng_manager", "ceo", "pm", "cdc", "member"].includes(user.role);
  const canEsign = ["admin", "eng_manager", "ceo", "pm", "member", "cdc"].includes(user.role);
  const canRectify = ["admin", "eng_manager", "member", "ceo"].includes(user.role);
  const canStage = ["admin", "eng_manager", "member", "pm", "cdc", "ceo"].includes(user.role);  // 与后端 STAGE_TEAM_ROLES 一致
  const [rects, setRects] = useState([]);
  const [newRect, setNewRect] = useState({ title: "", detail: "" });
  const [busy, setBusy] = useState(false);
  const token = authStore.get("ali_token");
  const fileUrl = (fid) => `${BACKEND}/api/files/${fid}/download?auth=${token}`;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/subcontracts/${sc.id}/rectifications`); setRects(data || []); } catch { setRects([]); }
  }, [api, sc.id]);
  useEffect(() => { load(); }, [load]);

  const createRect = async () => {
    if (!newRect.title.trim() && !newRect.detail.trim()) { toast.error(zh ? "请填写整改内容" : "Enter detail"); return; }
    setBusy(true);
    try { await api.post(`/subcontracts/${sc.id}/rectifications`, newRect); setNewRect({ title: "", detail: "" }); toast.success(zh ? "整改意见已下发" : "Rectification sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const resolveRect = async (rid) => {
    try { await api.post(`/rectifications/${rid}/resolve`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="border-t border-slate-100 pt-2.5 mt-1 space-y-2.5" data-testid={`sub-team-panel-${sc.id}`}>
      <div hidden={!!section && section !== "contract"} className="space-y-3">
      {/* 选择公司即复用已有账号；旧合同打开时补关联，不再保留手工分配入口。 */}
      {canManage && <SubcontractAccountStatus sc={sc} api={api} zh={zh} reload={reload} />}

      {/* 合同签署（公开链接）+ 安排开工 */}
      {canEsign && <SignLinkBlock sc={sc} api={api} zh={zh} token={token} reload={reload} />}
      <UnsignedPaymentApproval sc={sc} api={api} zh={zh} reload={reload} />


      {/* Quote review: 预算内 PM 直批 / 超预算转工程部经理 */}
      {(sc.quoteStatus === "submitted" || sc.quoteStatus === "eng_review") && canManage && <QuoteReviewBlock sc={sc} api={api} zh={zh} reload={reload} role={user.role} />}

      </div>
      <div hidden={!!section && section !== "records"}>
      {/* 完工验收资料（分类） */}
      <CompletionDocsTeam sc={sc} api={api} zh={zh} fileUrl={fileUrl} reload={reload} canUpload={canEsign} canPublish={canRectify} />

      </div>
      <div hidden={!!section && section !== "construction"} className="space-y-4">
      {canEsign && <StartWorkBlock sc={sc} api={api} zh={zh} reload={reload} role={user.role} />}
      {/* Construction stages (from payment milestones) */}
      <div data-testid={`sub-stages-team-${sc.id}`}>
        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1"><CalendarDays className="h-3.5 w-3.5 text-red-600" />{zh ? "施工阶段调度" : "Stage scheduling"}</p>
        <div className="space-y-1.5">
          {(sc.paymentPlan || []).map((m) => <StageRowTeam key={m.id} sc={sc} m={m} api={api} zh={zh} reload={reload} canManage={canStage} />)}
        </div>
      </div>

      {/* Rectifications */}
      {canRectify && (
        <div data-testid={`sub-rects-team-${sc.id}`}>
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1"><ClipboardCheck className="h-3.5 w-3.5 text-red-600" />{zh ? "整改意见" : "Rectifications"} ({rects.length})</p>
          {sc.subcontractorUserId ? (
            <div className="flex flex-wrap gap-2 mb-2">
              <Input data-testid={`sub-rect-title-${sc.id}`} placeholder={zh ? "整改标题" : "title"} value={newRect.title} onChange={(e) => setNewRect((r) => ({ ...r, title: e.target.value }))} className="h-9 text-xs w-32" />
              <Input placeholder={zh ? "整改说明" : "detail"} value={newRect.detail} onChange={(e) => setNewRect((r) => ({ ...r, detail: e.target.value }))} className="h-9 text-xs flex-1" />
              <Button data-testid={`sub-rect-send-${sc.id}`} size="sm" disabled={busy} className="h-9 text-xs bg-brand-500 hover:bg-brand-600 text-white" onClick={createRect}><Send className="h-3 w-3 mr-1" />{zh ? "下发" : "Send"}</Button>
            </div>
          ) : <p className="text-xs text-slate-400 mb-1">{zh ? "公司账号绑定后可下发整改；未开户请到分包商管理开通。" : "Rectifications are available once the company account is linked. Create missing accounts in Subcontractors."}</p>}
          <div className="space-y-1.5">
            {rects.map((rc) => (
              <div key={rc.id} className={`text-xs rounded border px-2 py-1.5 ${rc.status === "resolved" ? "border-green-200 bg-green-50/50" : "border-amber-200 bg-amber-50/40"}`} data-testid={`sub-rect-team-${rc.id}`}>
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-slate-700">{rc.title}</span>
                  <span className={`text-xs px-1.5 rounded-full ${rc.status === "resolved" ? "bg-green-100 text-green-700" : rc.status === "replied" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>{rc.status === "resolved" ? (zh ? "已关闭" : "resolved") : rc.status === "replied" ? (zh ? "已回复" : "replied") : (zh ? "待整改" : "open")}</span>
                  {rc.status !== "resolved" && <button data-testid={`sub-rect-resolve-${rc.id}`} onClick={() => resolveRect(rc.id)} className="ml-auto text-green-600 hover:underline">{zh ? "关闭" : "resolve"}</button>}
                </div>
                {rc.detail && <p className="text-slate-500 mt-0.5">{rc.detail}</p>}
                {(rc.replies || []).map((rp) => (
                  <div key={rp.id} className="mt-1 bg-white rounded px-1.5 py-1 border border-slate-100">
                    <span className="font-medium">{rp.by}</span>: {rp.text}
                    {(rp.images || []).length > 0 && <div className="flex gap-1 mt-1">{rp.images.map((im, i) => <a key={i} href={fileUrl(im.fileId)} target="_blank" rel="noreferrer" className="h-9 w-9 rounded overflow-hidden border"><img src={fileUrl(im.fileId)} alt="" className="h-full w-full object-cover" /></a>)}</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function StageRowTeam({ sc, m, api, zh, reload, canManage }) {
  const [busy, setBusy] = useState(false);
  const [insp, setInsp] = useState(false);
  const loc = useLocation();
  const autoRef = useRef(false);
  const st = STAGE_LABEL[m.stageStatus || "pending"] || STAGE_LABEL.pending;
  const status = m.stageStatus || "pending";
  const inspectable = status === "completed_pending" || status === "inspecting";
  // 铃铛「完工待检」深链 ?sub=&ms=&inspect=1 → 直接弹出安排检查（只弹一次）
  useEffect(() => {
    if (autoRef.current || !canManage || !inspectable) return;
    const q = new URLSearchParams(loc.search);
    if (q.get("sub") === sc.id && q.get("ms") === m.id && q.get("inspect") === "1") { autoRef.current = true; setInsp(true); }
  }, [loc.search, sc.id, m.id, canManage, inspectable]);
  const act = async (path, bodyObj) => {
    setBusy(true);
    try { await api.post(`/subcontracts/${sc.id}/stages/${m.id}/${path}`, bodyObj || {}); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="text-xs bg-slate-50 rounded-md px-2 py-1.5" data-testid={`stage-team-${m.id}`} data-status={status}>
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-slate-700 flex-1 truncate">{m.name}</span>
        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${st.cls}`}>{zh ? st.zh : st.en}</span>
        {(status === "pending" || status === "closed") && canManage && (
          <Button data-testid={`stage-notify-${m.id}`} size="sm" className="h-9 px-2 text-xs bg-brand-500 hover:bg-brand-600 text-white" disabled={busy} onClick={() => act("notify")}><Bell className="h-3 w-3 mr-1" />{zh ? "通知进入" : "Notify"}</Button>
        )}
        {status === "completed_pending" && canManage && (
          <span className="text-purple-600">{zh ? "请安排检查↓" : "schedule↓"}</span>
        )}
      </div>
      {(m.actualStart || m.expectedEnd) && <p className="text-slate-500 mt-0.5">{zh ? "开工" : "Start"} {m.actualStart || "-"} · {zh ? "完工" : "End"} {m.expectedEnd || m.actualEnd || "-"}</p>}
      {inspectable && canManage && (
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <Button data-testid={`stage-schedule-${m.id}`} size="sm" className="h-9 px-2 text-xs bg-purple-600 hover:bg-purple-700 text-white" disabled={busy} onClick={() => setInsp(true)}><CalendarCheck className="h-3 w-3 mr-1" />{zh ? (status === "inspecting" ? "改检查日期 / 验收" : "安排检查") : "Schedule inspection"}</Button>
          <Button data-testid={`stage-close-${m.id}`} size="sm" className="h-9 px-2 text-xs bg-green-600 hover:bg-green-700 text-white" disabled={busy} onClick={() => act("close")}><ShieldCheck className="h-3 w-3 mr-1" />{zh ? "验收关闭" : "Close"}</Button>
        </div>
      )}
      {status === "inspecting" && (m.companyInspection || m.municipalInspection) && (
        <p className="text-slate-500 mt-0.5" data-testid={`stage-insp-dates-${m.id}`}>{m.companyInspection && <>🏢 {m.companyInspection} </>}{m.municipalInspection && <span className="text-red-600">🏛 {zh ? "市政府" : "Muni"} {m.municipalInspection}</span>}</p>
      )}
      {insp && <StageInspectDialog open onClose={() => setInsp(false)} sc={sc} m={m} api={api} zh={zh} reload={reload} />}
    </div>
  );
}
